import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, ReferenceLine } from "recharts";
import { Scale, Ruler, Percent, Target, TrendingDown, TrendingUp } from "lucide-react";

const thisMonth = () => new Date().toISOString().slice(0, 7);
const fmtMonth = (m) => {
  if (!m) return "—";
  const [y, mo] = m.split("-");
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString(undefined, { month: "short", year: "numeric" });
};

const METRICS = [
  { key: "weight_kg",    goalKey: "goal_weight_kg",    startKey: "starting_weight_kg",    label: "Weight",       unit: "kg", icon: Scale,  step: 0.1, chartMin: 60,  chartMax: 130, direction: "down" },
  { key: "waist_cm",     goalKey: "goal_waist_cm",     startKey: "starting_waist_cm",     label: "Waist",        unit: "cm", icon: Ruler,  step: 0.5, chartMin: 60,  chartMax: 130, direction: "down" },
  { key: "body_fat_pct", goalKey: "goal_body_fat_pct", startKey: "starting_body_fat_pct", label: "Body Fat",     unit: "%",  icon: Percent, step: 0.5, chartMin: 8,   chartMax: 45,  direction: "down" },
];

export default function BodyMetrics() {
  const { user, refresh } = useAuth();
  const [metrics, setMetrics] = useState([]);
  const [logForm, setLogForm] = useState({ weight_kg: "", waist_cm: "", body_fat_pct: "" });
  const [goalForm, setGoalForm] = useState({
    goal_weight_kg: "", goal_waist_cm: "", goal_body_fat_pct: "",
    starting_weight_kg: "", starting_waist_cm: "", starting_body_fat_pct: "",
  });
  const [editGoals, setEditGoals] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/body-metrics");
      setMetrics(r.data);
      const current = r.data.find((m) => m.month === thisMonth());
      if (current) {
        setLogForm({
          weight_kg:    current.weight_kg    ?? "",
          waist_cm:     current.waist_cm     ?? "",
          body_fat_pct: current.body_fat_pct ?? "",
        });
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!user) return;
    setGoalForm({
      goal_weight_kg:        user.goal_weight_kg        ?? "",
      goal_waist_cm:         user.goal_waist_cm         ?? "",
      goal_body_fat_pct:     user.goal_body_fat_pct     ?? "",
      starting_weight_kg:    user.starting_weight_kg    ?? "",
      starting_waist_cm:     user.starting_waist_cm     ?? "",
      starting_body_fat_pct: user.starting_body_fat_pct ?? "",
    });
  }, [user]);

  const saveMonth = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const payload = { month: thisMonth() };
      ["weight_kg", "waist_cm", "body_fat_pct"].forEach((k) => {
        if (logForm[k] !== "" && logForm[k] !== null && logForm[k] !== undefined) {
          payload[k] = Number(logForm[k]);
        }
      });
      await api.post("/body-metrics", payload);
      toast.success(`${fmtMonth(thisMonth())} logged.`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const saveGoals = async () => {
    setBusy(true);
    try {
      const payload = {};
      Object.entries(goalForm).forEach(([k, v]) => {
        if (v !== "" && v !== null && v !== undefined) payload[k] = Number(v);
      });
      await api.put("/user/goals", payload);
      await refresh();
      toast.success("Goals saved.");
      setEditGoals(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const chartRows = useMemo(() => {
    return metrics.map((m) => ({
      month: fmtMonth(m.month).replace(" ", " '").slice(0, 7),
      weight_kg: m.weight_kg ?? null,
      waist_cm: m.waist_cm ?? null,
      body_fat_pct: m.body_fat_pct ?? null,
    }));
  }, [metrics]);

  const progressFor = (metric) => {
    const start = user?.[metric.startKey];
    const goal  = user?.[metric.goalKey];
    const latest = [...metrics].reverse().find((m) => m[metric.key] != null)?.[metric.key];
    if (start == null || goal == null || latest == null) return null;
    const totalDelta = start - goal;    // positive = need to lose
    const currentDelta = start - latest;
    if (totalDelta === 0) return null;
    const pct = Math.max(0, Math.min(100, Math.round((currentDelta / totalDelta) * 100)));
    const remaining = Math.round((latest - goal) * 10) / 10;
    return { start, goal, latest, pct, remaining, onTrack: currentDelta >= 0 };
  };

  return (
    <div className="space-y-6" data-testid="body-metrics">
      {/* Header + goals editor toggle */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow">Body Metrics · Monthly Check-in</div>
          <h2 className="wordmark mt-2 text-3xl uppercase">Weight. Waist. Body Fat.</h2>
          <p className="text-sm text-muted-foreground mt-1">Log once a month. Track the trend against your goal — not the daily noise.</p>
        </div>
        <button onClick={() => setEditGoals((s) => !s)} className="btn-secondary btn-sm" data-testid="edit-goals-btn">
          <Target size={14} /> {editGoals ? "Close" : "Set Goals"}
        </button>
      </div>

      {/* Goals editor */}
      {editGoals && (
        <div className="bg-card border border-border rounded-lg p-6" data-testid="goals-editor">
          <div className="label-eyebrow">Your Goals & Starting Point</div>
          <p className="text-xs text-muted-foreground mt-1">Starting values anchor your progress bar. Goals set your target line on the trend chart.</p>
          <div className="mt-5 grid md:grid-cols-3 gap-4">
            {METRICS.map((m) => (
              <div key={m.key} className="space-y-3">
                <div className="font-head uppercase text-sm">{m.label} ({m.unit})</div>
                <label className="block text-[11px] uppercase tracking-widest text-muted-foreground">Starting</label>
                <input type="number" step={m.step} value={goalForm[m.startKey]}
                  onChange={(e) => setGoalForm({ ...goalForm, [m.startKey]: e.target.value })}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  data-testid={`start-${m.key}`} />
                <label className="block text-[11px] uppercase tracking-widest text-muted-foreground">Goal</label>
                <input type="number" step={m.step} value={goalForm[m.goalKey]}
                  onChange={(e) => setGoalForm({ ...goalForm, [m.goalKey]: e.target.value })}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  data-testid={`goal-${m.key}`} />
              </div>
            ))}
          </div>
          <button onClick={saveGoals} disabled={busy} className="btn-primary btn-sm mt-6" data-testid="save-goals-btn">
            {busy ? "Saving…" : "Save Goals"}
          </button>
        </div>
      )}

      {/* Progress cards */}
      <div className="grid md:grid-cols-3 gap-3">
        {METRICS.map((m) => {
          const p = progressFor(m);
          return (
            <div key={m.key} className="bg-card border border-border rounded-lg p-5" data-testid={`progress-${m.key}`}>
              <div className="flex items-center justify-between">
                <div className="label-eyebrow flex items-center gap-2"><m.icon size={14} strokeWidth={2.5} /> {m.label}</div>
                {p && (p.onTrack ? <TrendingDown className="text-primary" size={16} /> : <TrendingUp className="text-foreground/60" size={16} />)}
              </div>
              {p ? (
                <>
                  <div className="mt-3 flex items-baseline gap-2">
                    <div className="wordmark text-4xl leading-none text-foreground">{p.latest}<span className="text-lg text-muted-foreground"> {m.unit}</span></div>
                  </div>
                  <div className="mt-2 text-[11px] uppercase tracking-widest text-muted-foreground">
                    Start {p.start}{m.unit} → Goal {p.goal}{m.unit}
                  </div>
                  <div className="mt-4 h-2 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-primary transition-all" style={{ width: `${p.pct}%` }} />
                  </div>
                  <div className="mt-2 flex justify-between text-[11px] uppercase tracking-widest">
                    <span className="text-primary font-semibold">{p.pct}% to goal</span>
                    <span className="text-muted-foreground">{p.remaining > 0 ? `${p.remaining}${m.unit} to go` : "Goal reached"}</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="mt-3 wordmark text-4xl leading-none text-muted-foreground">—</div>
                  <div className="mt-3 text-xs text-muted-foreground">Set a starting value & goal, then log this month.</div>
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Monthly log form */}
      <form onSubmit={saveMonth} className="bg-foreground text-background rounded-lg p-6 md:p-8" data-testid="monthly-log-form">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="label-eyebrow !text-background/70">Log · {fmtMonth(thisMonth())}</div>
            <div className="wordmark mt-2 text-3xl uppercase">This Month's Check-in.</div>
          </div>
          <div className="text-xs text-background/70 max-w-xs">Only one entry per month — you can update this month's numbers any time.</div>
        </div>
        <div className="mt-6 grid md:grid-cols-3 gap-4">
          {METRICS.map((m) => (
            <div key={m.key}>
              <label className="label-eyebrow !text-background/70 flex items-center gap-2"><m.icon size={12} strokeWidth={2.5} /> {m.label} ({m.unit})</label>
              <input
                type="number" step={m.step}
                value={logForm[m.key]}
                onChange={(e) => setLogForm({ ...logForm, [m.key]: e.target.value })}
                className="mt-2 w-full rounded-md border border-background/20 bg-background/10 px-3 py-2.5 text-sm text-background placeholder:text-background/40 focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="—"
                data-testid={`input-${m.key}`}
              />
            </div>
          ))}
        </div>
        <button disabled={busy} className="btn-primary btn-sm mt-6" data-testid="save-metrics-btn">
          {busy ? "Saving…" : `Save ${fmtMonth(thisMonth())}`}
        </button>
      </form>

      {/* Trend charts */}
      <div className="grid lg:grid-cols-3 gap-3">
        {METRICS.map((m) => {
          const rows = chartRows.filter((r) => r[m.key] != null).map((r) => ({ month: r.month, value: r[m.key] }));
          const goal = user?.[m.goalKey];
          const values = rows.map((r) => r.value);
          const dataMin = values.length ? Math.min(...values, goal ?? Infinity) : m.chartMin;
          const dataMax = values.length ? Math.max(...values, goal ?? -Infinity) : m.chartMax;
          const pad = (dataMax - dataMin) * 0.15 || 2;
          return (
            <div key={m.key} className="bg-card border border-border rounded-lg p-5" data-testid={`chart-${m.key}`}>
              <div className="flex items-center justify-between">
                <div className="label-eyebrow flex items-center gap-2"><m.icon size={14} strokeWidth={2.5} /> {m.label} Trend</div>
                {goal != null && <div className="text-[11px] uppercase tracking-widest text-primary">Goal {goal}{m.unit}</div>}
              </div>
              <div className="h-48 mt-3">
                {rows.length > 0 ? (
                  <ResponsiveContainer>
                    <LineChart data={rows} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="month" fontSize={10} stroke="hsl(var(--muted-foreground))" />
                      <YAxis domain={[Math.max(0, dataMin - pad), dataMax + pad]} fontSize={10} stroke="hsl(var(--muted-foreground))" />
                      <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }} />
                      {goal != null && <ReferenceLine y={goal} stroke="hsl(var(--primary))" strokeDasharray="4 4" label={{ value: "Goal", fontSize: 10, fill: "hsl(var(--primary))" }} />}
                      <Line type="monotone" dataKey="value" stroke="hsl(var(--foreground))" strokeWidth={2} dot={{ r: 4, fill: "hsl(var(--primary))" }} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full grid place-items-center text-xs text-muted-foreground">
                    Log this month to start the trend.
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* History table */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="label-eyebrow">Monthly History</div>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">
                <th className="py-2 pr-4">Month</th>
                <th className="py-2 pr-4">Weight (kg)</th>
                <th className="py-2 pr-4">Waist (cm)</th>
                <th className="py-2 pr-4">Body Fat (%)</th>
              </tr>
            </thead>
            <tbody>
              {[...metrics].reverse().map((m) => (
                <tr key={m.month} className="border-b border-border/60" data-testid={`row-${m.month}`}>
                  <td className="py-2 pr-4">{fmtMonth(m.month)}</td>
                  <td className="py-2 pr-4">{m.weight_kg ?? "—"}</td>
                  <td className="py-2 pr-4">{m.waist_cm ?? "—"}</td>
                  <td className="py-2 pr-4">{m.body_fat_pct ?? "—"}</td>
                </tr>
              ))}
              {metrics.length === 0 && <tr><td colSpan="4" className="py-4 text-muted-foreground">No monthly logs yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
